import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import {
  API_BASE_FILE_URL,
  FILE_API_URL,
  FunctionType,
  PdfViewer,
  ShowMessageService,
} from '@olymp/shared';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { catchError, of } from 'rxjs';

@Component({
  selector: 'lib-file-upload',
  imports: [TranslocoModule, ButtonModule, PdfViewer, TooltipModule],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadComponent),
      multi: true,
    },
  ],
})
export class FileUploadComponent implements ControlValueAccessor {
  filePreviewUrl = signal<string | null>(null);
  fileName = signal<string | null>(null);
  uploadedFile = signal<File | null>(null);
  /** Serverdagi fayl id (download sharti uchun). */
  protected readonly serverFileId = signal<string | null>(null);
  /** Serverdan kelgan blob MIME turi. */
  private readonly serverBlobType = signal<string>('');
  private fetchingId: string | null = null;

  httpClient = inject(HttpClient);
  baseURL = inject(API_BASE_FILE_URL);
  $msgService = inject(ShowMessageService);

  isSeeOnly = input<boolean>(false);
  acceptFileTypes = input<string>('.pdf');
  /**
   * false bo‘lsa serverdagi faylni ko‘rish/yuklab olish bloklanadi.
   * Lokal (yangi tanlangan) File ga ta’sir qilmaydi.
   */
  canDownload = input<boolean>(true);
  downloadTooltip = input<string>('');

  readonly isPdfPreview = computed(() => {
    const localType = this.uploadedFile()?.type ?? '';
    if (localType) {
      return localType === 'application/pdf';
    }
    const serverType = this.serverBlobType().toLowerCase();
    if (serverType.includes('pdf')) {
      return true;
    }
    // MIME bo‘sh bo‘lsa — faqat accept faqat pdf bo‘lsa PDF deb hisobla
    const accept = this.acceptFileTypes().toLowerCase();
    const allowsPdf = accept.includes('.pdf') || accept.includes('pdf');
    const allowsDoc =
      accept.includes('.doc') ||
      accept.includes('word') ||
      accept.includes('officedocument');
    return allowsPdf && !allowsDoc;
  });

  readonly isWordPreview = computed(() => {
    const localType = this.uploadedFile()?.type ?? '';
    if (localType) {
      return localType.includes('word') || localType.includes('document');
    }
    const serverType = this.serverBlobType().toLowerCase();
    if (
      serverType.includes('word') ||
      serverType.includes('officedocument') ||
      serverType.includes('msword')
    ) {
      return true;
    }
    const accept = this.acceptFileTypes().toLowerCase();
    return accept.includes('.doc') || accept.includes('word');
  });

  constructor() {
    effect(() => {
      const allowed = this.canDownload();
      const fileId = this.serverFileId();
      if (!fileId || this.uploadedFile()) {
        return;
      }

      if (allowed) {
        if (!this.filePreviewUrl() && this.fetchingId !== fileId) {
          this.getFiles(fileId);
        }
      } else {
        this.revokePreviewUrl();
        this.filePreviewUrl.set(null);
        this.fileName.set(null);
        this.serverBlobType.set('');
      }
    });
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];

      if (!this.isFileTypeAllowed(file)) {
        this.$msgService.errorMsg('invalid_file_type');
        input.value = '';
        return;
      }

      this.serverFileId.set(null);
      this.serverBlobType.set('');
      this.fetchingId = null;
      this.onChange(file);
      this.createFilePreviewUrl(file);
    }
  }

  private isFileTypeAllowed(file: File): boolean {
    const allowedTypes = this.acceptFileTypes()
      .split(',')
      .map((type) => type.trim());
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    const fileType = file.type;

    return allowedTypes.some((type) => {
      if (type.startsWith('.')) {
        return fileExtension === type.toLowerCase();
      }
      return fileType.includes(type);
    });
  }

  private createFilePreviewUrl(file: File): void {
    this.fileName.set(file.name);
    this.uploadedFile.set(file);
    this.serverBlobType.set(file.type || '');
    this.revokePreviewUrl();
    const blob = new Blob([file], { type: file.type });
    const url = URL.createObjectURL(blob);
    this.filePreviewUrl.set(url);
  }

  private revokePreviewUrl(): void {
    const current = this.filePreviewUrl();
    if (current?.startsWith('blob:')) {
      URL.revokeObjectURL(current);
    }
  }

  onChange: FunctionType = () => {};
  onTouched: FunctionType = () => {};

  writeValue(obj: any): void {
    if (obj) {
      if (obj instanceof File) {
        this.serverFileId.set(null);
        this.fetchingId = null;
        this.createFilePreviewUrl(obj);
        return;
      }
      if (typeof obj === 'string' && obj.trim()) {
        // Faqat id ni o‘rnatamiz — yuklash effect orqali bir marta ketadi
        this.uploadedFile.set(null);
        this.fetchingId = null;
        this.revokePreviewUrl();
        this.filePreviewUrl.set(null);
        this.fileName.set(null);
        this.serverBlobType.set('');
        this.serverFileId.set(obj);
        return;
      }
      this.resetState();
      return;
    }
    this.resetState();
  }

  private resetState(): void {
    this.serverFileId.set(null);
    this.fetchingId = null;
    this.revokePreviewUrl();
    this.filePreviewUrl.set(null);
    this.fileName.set(null);
    this.uploadedFile.set(null);
    this.serverBlobType.set('');
  }

  registerOnChange(fn: FunctionType): void {
    this.onChange = fn;
  }

  getFiles(id: string) {
    if (!id || typeof id !== 'string') {
      return;
    }
    if (this.fetchingId === id) {
      return;
    }

    this.fetchingId = id;
    this.httpClient
      .get(this.baseURL + FILE_API_URL.download_file.replace('id:', id), {
        responseType: 'blob',
      })
      .pipe(
        catchError((error) => {
          console.warn('File not found or error loading file:', id, error);
          if (this.fetchingId === id) {
            this.fetchingId = null;
          }
          this.filePreviewUrl.set(null);
          this.fileName.set(null);
          this.serverBlobType.set('');
          return of(null);
        }),
      )
      .subscribe((file) => {
        if (this.fetchingId === id) {
          this.fetchingId = null;
        }
        if (!file) {
          return;
        }
        if (!this.canDownload() && this.serverFileId() === id) {
          return;
        }
        if (this.serverFileId() !== id) {
          return;
        }

        this.revokePreviewUrl();
        this.serverBlobType.set(file.type || '');
        const url = URL.createObjectURL(file);
        this.filePreviewUrl.set(url);
        this.fileName.set(this.resolveServerFileName(file.type));
      });
  }

  private resolveServerFileName(mimeType: string): string {
    const type = (mimeType || '').toLowerCase();
    if (type.includes('pdf')) {
      return 'uploaded_file.pdf';
    }
    if (type.includes('word') || type.includes('officedocument')) {
      return 'uploaded_file.docx';
    }
    const accept = this.acceptFileTypes().toLowerCase();
    if (accept.includes('.docx')) {
      return 'uploaded_file.docx';
    }
    if (accept.includes('.doc')) {
      return 'uploaded_file.doc';
    }
    if (accept.includes('.pdf')) {
      return 'uploaded_file.pdf';
    }
    return 'uploaded_file';
  }

  registerOnTouched(fn: FunctionType): void {
    this.onTouched = fn;
  }
}
